import { useCallback } from 'react';
import type { ReactElement } from 'react';

import type { GetDocumentExtractionOutput } from 'korint-sdk';
import type { AIClassificationDisplayConfig, DocumentClassificationState } from 'korint-sdk/react';

import { AIClassificationBadge } from './DocumentClassificationStatus';
import { parseAIClassificationMetadata } from './DocumentClassificationStatus';

type WithMetadata = { id: string; metadata: Record<string, unknown>; signedUrl?: string };

interface UseDocumentAiBadgesArgs<TDoc extends WithMetadata> {
  classificationStates: Map<string, DocumentClassificationState>;
  displayConfig: AIClassificationDisplayConfig | undefined;
  extractionEnabled: boolean;
  getExtraction: (documentId: string) => GetDocumentExtractionOutput | undefined;
  /** Opens the viewer; skipped when the document has no signedUrl yet. */
  onOpen?: (doc: TDoc) => void;
}

/** Validity isn't shown here — redundant with the viewer's status; callers keep it for the
 * viewer only. */
export function useRenderDocumentAiBadges<TDoc extends WithMetadata>({
  classificationStates,
  displayConfig,
  extractionEnabled,
  getExtraction,
  onOpen,
}: UseDocumentAiBadgesArgs<TDoc>): (doc: TDoc) => ReactElement {
  return useCallback(
    (doc: TDoc) => {
      const extraction = extractionEnabled ? getExtraction(doc.id) : undefined;
      return (
        <AIClassificationBadge
          classificationState={classificationStates.get(doc.id)}
          metadataClassification={parseAIClassificationMetadata(doc.metadata?.aiclassification)}
          displayConfig={displayConfig?.enabled ? displayConfig : undefined}
          extractionData={
            extraction
              ? {
                  status: extraction.status as 'PENDING' | 'DONE' | 'FAILED' | 'SKIPPED',
                  fields: extraction.fields as Record<string, unknown> | undefined,
                  errorMessage: extraction.errorMessage,
                }
              : undefined
          }
          onOpen={onOpen && doc.signedUrl ? () => onOpen(doc) : undefined}
        />
      );
    },
    [classificationStates, displayConfig, extractionEnabled, getExtraction, onOpen],
  );
}
